import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Edit, Trash2, CreditCard, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/hooks/use-toast";
import {
  listPlans,
  deletePlan,
  type Plan,
} from "@/services/plansService";
import { PlanForm } from "./PlanForm";
import { usePermissions } from "@/hooks/usePermissions";
import { PermissionGuard } from "@/components/auth/PermissionGuard";
import {
  formatPlanInteger,
  formatPlanRate,
} from "@/utils/planDisplay";

interface PlanManagementProps {
  onBack: () => void;
}

type ViewMode = "list" | "form";

export const PlanManagement = ({ onBack }: PlanManagementProps) => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { canCreate, canUpdate, canDelete } = usePermissions();

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const response = await listPlans({ limit: 100 });
      if (response.success && response.data) {
        setPlans(response.data.data || []);
      }
    } catch (error) {
      toast({
        title: "Erro ao carregar planos",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSavePlan = async () => {
    await loadPlans();
    setViewMode("list");
    setEditingPlan(null);
  };

  const handleDelete = async (planId: string) => {
    if (!confirm("Tem certeza que deseja remover este plano?")) {
      return;
    }

    try {
      const response = await deletePlan(planId);
      if (response.success) {
        toast({
          title: "Sucesso",
          description: "Plano removido com sucesso",
        });
        await loadPlans();
      } else {
        throw new Error(response.error?.message || "Erro ao remover plano");
      }
    } catch (error) {
      toast({
        title: "Erro",
        description:
          error instanceof Error ? error.message : "Erro ao remover plano",
        variant: "destructive",
      });
    }
  };

  if (viewMode === "form") {
    return (
      <PlanForm
        onBack={() => setViewMode("list")}
        onSave={handleSavePlan}
        editingPlan={editingPlan}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando planos...</p>
        </div>
      </div>
    );
  }

  return (
    <PermissionGuard
      permission="res_plans.read"
      fallback={
        <div className="min-h-screen flex items-center justify-center p-8">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle>Acesso negado</CardTitle>
              <CardDescription>
                Você não tem permissão para visualizar planos.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      }
    >
      <div className="min-h-screen bg-gradient-to-br from-background to-muted">
        <PageHeader
          icon={CreditCard}
          title="Catálogo de Planos"
          description="Planos fixos disponíveis para contratação"
          onBack={onBack}
          actions={
            canCreate("plans") && (
              <Button
                className="bg-gradient-primary text-white hover:opacity-90"
                onClick={() => {
                  setEditingPlan(null);
                  setViewMode("form");
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Novo Plano
              </Button>
            )
          }
        />

        <main className="container mx-auto px-6 py-6">
          <Card>
            <CardHeader>
              <CardTitle>Planos cadastrados</CardTitle>
              <CardDescription>
                Planos comerciais disponíveis para atribuição aos clientes.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Acionamentos / mês</TableHead>
                    <TableHead>Tokens / mês</TableHead>
                    <TableHead>Excedente (R$ / 1M)</TableHead>
                    <TableHead>Limite 1 (%)</TableHead>
                    <TableHead>Limite 2 (%)</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plans.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        Nenhum plano cadastrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    plans.map((plan) => (
                      <TableRow key={plan.id}>
                        <TableCell className="font-medium">{plan.name}</TableCell>
                        <TableCell>
                          {formatPlanInteger(plan.monthly_call_limit)}
                        </TableCell>
                        <TableCell>
                          {formatPlanInteger(plan.monthly_token_limit)}
                        </TableCell>
                        <TableCell>
                          {formatPlanRate(plan.excess_rate_per_1m_tokens)}
                        </TableCell>
                        <TableCell>
                          {plan.alert_threshold_1_percent == null
                            ? "—"
                            : `${plan.alert_threshold_1_percent}%`}
                        </TableCell>
                        <TableCell>
                          {plan.alert_threshold_2_percent == null
                            ? "—"
                            : `${plan.alert_threshold_2_percent}%`}
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          {canUpdate("plans") && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingPlan(plan);
                                setViewMode("form");
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          {canDelete("plans") && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(plan.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </main>
      </div>
    </PermissionGuard>
  );
};
